import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Download,
  Search,
  X,
} from "lucide-react";
import {
  BARCODE_INSTANCE_LIMIT,
  BARCODE_PARENT_PAGE_SIZE,
  fetchBarcodeTrackingPage,
} from "./barcodeTrackingData";
import {
  BARCODE_OVERALL_STATUSES,
  buildMovementTimeline,
  exportBarcodeTrackingWorkbook,
  filterBarcodeRows,
  formatUkDateTime,
} from "./barcodeTrackingHelper";

const EMPTY_FILTERS = {
  serialNumber: "",
  barcodeNumber: "",
  productCode: "",
  printedFrom: "",
  printedTo: "",
  overallStatus: "",
};

const INPUT_CLASS =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary";

function text(value) {
  return value === "" || value == null ? "—" : String(value);
}

function formatRawField(value) {
  if (value == null) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (
    value instanceof Date ||
    typeof value?.toDate === "function" ||
    Number.isFinite(value?.seconds)
  ) {
    return formatUkDateTime(value) || "—";
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function OverallStatus({ status }) {
  const inconsistent = status === "Inconsistent";
  const completed = status === "Completed";
  const classes = inconsistent
    ? "border-amber-300 bg-amber-50 text-amber-800"
    : completed
    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
    : "border-gray-300 bg-gray-50 text-gray-700";

  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold ${classes}`}
    >
      {inconsistent ? (
        <AlertTriangle size={14} aria-hidden="true" />
      ) : completed ? (
        <CheckCircle2 size={14} aria-hidden="true" />
      ) : (
        <Clock3 size={14} aria-hidden="true" />
      )}
      {status}
    </span>
  );
}

function ScanStatus({ row, field, scanned, timestamp }) {
  const inconsistent = row.inconsistencyFields.includes(field);
  if (inconsistent) {
    return (
      <div className="min-w-[150px] text-amber-800">
        <div className="flex items-center gap-1 font-semibold">
          <AlertTriangle size={15} aria-hidden="true" />
          Inconsistent
        </div>
        {timestamp ? (
          <div className="mt-1 text-xs">{formatUkDateTime(timestamp)}</div>
        ) : null}
      </div>
    );
  }

  if (scanned) {
    return (
      <div className="min-w-[150px] text-emerald-700">
        <div className="flex items-center gap-1 font-semibold">
          <CheckCircle2 size={15} aria-hidden="true" />
          Completed
        </div>
        <div className="mt-1 text-xs">{formatUkDateTime(timestamp)}</div>
      </div>
    );
  }

  return <span className="whitespace-nowrap text-gray-500">Not scanned</span>;
}

function FieldList({ fields }) {
  const entries = Object.entries(fields || {}).sort(([a], [b]) =>
    a.localeCompare(b)
  );
  if (!entries.length) return <div className="text-sm text-gray-500">No fields.</div>;

  return (
    <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {entries.map(([key, value]) => (
        <div key={key} className="rounded-md border border-gray-200 p-3">
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {key}
          </dt>
          <dd className="mt-1 break-words text-sm text-gray-900">
            {formatRawField(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function DetailDrawer({ row, onClose }) {
  if (!row) return null;
  const timeline = buildMovementTimeline(row);

  return (
    <div className="fixed inset-0 z-[100]">
      <button
        type="button"
        aria-label="Close barcode details"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="barcode-detail-title"
        className="absolute right-0 top-0 flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-primary/20 bg-primary px-5 py-4 text-white">
          <div>
            <div className="text-sm text-white/80">Barcode instance</div>
            <h2 id="barcode-detail-title" className="text-xl font-bold">
              {text(row.barcodeNumber)}
            </h2>
            <div className="mt-1 text-sm text-white/80">
              Serial {text(row.serialNumber)} · Instance {text(row.instanceId)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 hover:bg-white/10"
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto p-5">
          <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
              <div className="text-xs font-semibold uppercase text-gray-500">
                Overall status
              </div>
              <div className="mt-2">
                <OverallStatus status={row.overallStatus} />
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 p-3 sm:col-span-2">
              <div className="text-xs font-semibold uppercase text-gray-500">
                Firestore document IDs
              </div>
              <div className="mt-2 break-all text-sm">
                Parent: <span className="font-mono">{row.parentId}</span>
              </div>
              <div className="mt-1 break-all text-sm">
                Instance: <span className="font-mono">{row.instanceId}</span>
              </div>
            </div>
          </section>

          {row.inconsistencies.length ? (
            <section className="rounded-lg border border-amber-300 bg-amber-50 p-4">
              <h3 className="flex items-center gap-2 font-semibold text-amber-900">
                <AlertTriangle size={18} />
                Inconsistencies
              </h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-900">
                {row.inconsistencies.map((issue, index) => (
                  <li key={`${issue.field}-${index}`}>{issue.message}</li>
                ))}
              </ul>
            </section>
          ) : null}

          <section>
            <h3 className="mb-3 text-lg font-semibold text-primary">
              Movement timeline
            </h3>
            {timeline.length ? (
              <ol className="space-y-3 border-l-2 border-primary/30 pl-5">
                {timeline.map((event) => (
                  <li key={event.key} className="relative">
                    <span
                      className={`absolute -left-[27px] top-1 h-3 w-3 rounded-full ${
                        event.inconsistent ? "bg-amber-500" : "bg-primary"
                      }`}
                    />
                    <div className="font-semibold text-gray-900">{event.label}</div>
                    <div className="text-sm text-gray-600">
                      {event.timestamp
                        ? formatUkDateTime(event.timestamp)
                        : "Timestamp missing"}
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="text-sm text-gray-500">No dated movement recorded.</div>
            )}
          </section>

          <section>
            <h3 className="mb-3 text-lg font-semibold text-primary">
              Parent document fields
            </h3>
            <FieldList fields={row.parentFields} />
          </section>

          <section>
            <h3 className="mb-3 text-lg font-semibold text-primary">
              Instance document fields
            </h3>
            <FieldList fields={row.instanceFields} />
          </section>
        </div>
      </aside>
    </div>
  );
}

export default function BarcodeTrackingReport() {
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadedParentCount, setLoadedParentCount] = useState(0);
  const [serverSearchField, setServerSearchField] = useState("BarcodeNumber");
  const [truncatedParentIds, setTruncatedParentIds] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setRows([]);
    setCursor(null);
    setHasMore(false);
    setLoadedParentCount(0);
    setTruncatedParentIds([]);

    fetchBarcodeTrackingPage({ filters: appliedFilters })
      .then((result) => {
        if (cancelled) return;
        setRows(result.rows);
        setCursor(result.cursor);
        setHasMore(result.hasMore);
        setLoadedParentCount(result.parentCount);
        setServerSearchField(result.serverSearchField);
        setTruncatedParentIds(result.truncatedParentIds);
      })
      .catch((loadError) => {
        if (cancelled) return;
        console.error("Barcode tracking load failed:", loadError);
        setError(
          loadError?.message ||
            "Barcode tracking records could not be loaded."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [appliedFilters]);

  const filteredRows = useMemo(
    () => filterBarcodeRows(rows, appliedFilters),
    [rows, appliedFilters]
  );

  const updateFilter = (field, value) => {
    setDraftFilters((current) => ({ ...current, [field]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    if (
      draftFilters.printedFrom &&
      draftFilters.printedTo &&
      draftFilters.printedFrom > draftFilters.printedTo
    ) {
      setError("Printed date-from must not be after date-to.");
      return;
    }
    setError("");
    setSelectedRow(null);
    setAppliedFilters({ ...draftFilters });
  };

  const clearFilters = () => {
    setSelectedRow(null);
    setDraftFilters({ ...EMPTY_FILTERS });
    setAppliedFilters({ ...EMPTY_FILTERS });
  };

  const loadMore = async () => {
    if (!cursor || loading) return;
    setLoading(true);
    setError("");
    try {
      const result = await fetchBarcodeTrackingPage({
        filters: appliedFilters,
        cursor,
      });
      setRows((current) => [...current, ...result.rows]);
      setCursor(result.cursor);
      setHasMore(result.hasMore);
      setLoadedParentCount((current) => current + result.parentCount);
      setTruncatedParentIds((current) => [
        ...new Set([...current, ...result.truncatedParentIds]),
      ]);
    } catch (loadError) {
      console.error("Barcode tracking pagination failed:", loadError);
      setError(
        loadError?.message || "More barcode records could not be loaded."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-gray-100">
      <header className="bg-primary px-6 py-6 text-white shadow-md">
        <div className="mx-auto max-w-[1600px]">
          <h1 className="text-3xl font-bold">Barcode Tracking</h1>
          <p className="mt-1 text-sm text-white/85">
            Read-only printing and movement history from factory barcode
            instances.
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-[1600px] space-y-5 p-6">
        <form
          onSubmit={applyFilters}
          className="rounded-xl border border-primary/20 bg-white p-5 shadow-sm"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
            <label className="text-sm font-semibold text-gray-700">
              Serial number
              <input
                value={draftFilters.serialNumber}
                onChange={(event) =>
                  updateFilter("serialNumber", event.target.value)
                }
                className={`${INPUT_CLASS} mt-1`}
                placeholder="Starts with…"
              />
            </label>
            <label className="text-sm font-semibold text-gray-700">
              Barcode number
              <input
                value={draftFilters.barcodeNumber}
                onChange={(event) =>
                  updateFilter("barcodeNumber", event.target.value)
                }
                className={`${INPUT_CLASS} mt-1`}
                placeholder="Starts with…"
              />
            </label>
            <label className="text-sm font-semibold text-gray-700">
              Product code
              <input
                value={draftFilters.productCode}
                onChange={(event) =>
                  updateFilter("productCode", event.target.value)
                }
                className={`${INPUT_CLASS} mt-1`}
                placeholder="Starts with…"
              />
            </label>
            <label className="text-sm font-semibold text-gray-700">
              Printed date-from
              <input
                type="date"
                value={draftFilters.printedFrom}
                onChange={(event) =>
                  updateFilter("printedFrom", event.target.value)
                }
                className={`${INPUT_CLASS} mt-1`}
              />
            </label>
            <label className="text-sm font-semibold text-gray-700">
              Printed date-to
              <input
                type="date"
                value={draftFilters.printedTo}
                onChange={(event) =>
                  updateFilter("printedTo", event.target.value)
                }
                className={`${INPUT_CLASS} mt-1`}
              />
            </label>
            <label className="text-sm font-semibold text-gray-700">
              Overall status
              <select
                value={draftFilters.overallStatus}
                onChange={(event) =>
                  updateFilter("overallStatus", event.target.value)
                }
                className={`${INPUT_CLASS} mt-1`}
              >
                <option value="">All statuses</option>
                {BARCODE_OVERALL_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-hover disabled:opacity-50"
            >
              <Search size={17} />
              Apply filters
            </button>
            <button
              type="button"
              onClick={clearFilters}
              disabled={loading}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Clear filters
            </button>
            <button
              type="button"
              onClick={() => exportBarcodeTrackingWorkbook(filteredRows)}
              disabled={!filteredRows.length}
              className="inline-flex items-center gap-2 rounded-md border border-primary bg-white px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/5 disabled:opacity-40"
            >
              <Download size={17} />
              Export filtered XLSX
            </button>
            <span className="text-sm text-gray-600">
              {filteredRows.length} instance
              {filteredRows.length === 1 ? "" : "s"} shown from{" "}
              {loadedParentCount} loaded parent
              {loadedParentCount === 1 ? "" : "s"}
            </span>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            Parent records load {BARCODE_PARENT_PAGE_SIZE} at a time. When
            several text searches are supplied, {serverSearchField} is queried
            in Firestore first and the remaining filters are applied to those
            loaded results.
          </p>
        </form>

        {error ? (
          <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800">
            {error}
          </div>
        ) : null}

        {truncatedParentIds.length ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            <div className="flex items-center gap-2 font-semibold">
              <AlertTriangle size={18} />
              Instance safety limit reached
            </div>
            <p className="mt-1">
              {truncatedParentIds.length} parent record
              {truncatedParentIds.length === 1 ? "" : "s"} had more than{" "}
              {BARCODE_INSTANCE_LIMIT} instances. Only the first{" "}
              {BARCODE_INSTANCE_LIMIT} are shown for each affected parent.
            </p>
          </div>
        ) : null}

        <section className="overflow-hidden rounded-xl border border-primary/20 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left text-sm">
              <thead className="bg-primary text-xs uppercase tracking-wide text-white">
                <tr>
                  {[
                    "Serial Number",
                    "Product Code",
                    "Barcode Number",
                    "Quantity",
                    "Program",
                    "Created At",
                    "Printed At",
                    "Stretch",
                    "Hortonwood",
                    "Despatch",
                    "Overall Status",
                  ].map((heading) => (
                    <th key={heading} className="whitespace-nowrap px-3 py-3">
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRows.map((row) => (
                  <tr
                    key={row.id}
                    tabIndex={0}
                    onClick={() => setSelectedRow(row)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedRow(row);
                      }
                    }}
                    className="cursor-pointer hover:bg-primary/5 focus:bg-primary/5 focus:outline-none"
                  >
                    <td className="whitespace-nowrap px-3 py-3 font-mono text-gray-800">
                      {text(row.serialNumber)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-gray-700">
                      {text(row.productCode)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 font-mono text-gray-800">
                      {text(row.barcodeNumber)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-gray-700">
                      {text(row.quantity)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-gray-700">
                      {text(row.program)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-gray-700">
                      {formatUkDateTime(row.createdAt) || "—"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-gray-700">
                      {row.printed
                        ? formatUkDateTime(row.printedAt) || "Timestamp missing"
                        : "Not printed"}
                    </td>
                    <td className="px-3 py-3">
                      <ScanStatus
                        row={row}
                        field="stretch"
                        scanned={row.stretchScan}
                        timestamp={row.stretchScanTime}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <ScanStatus
                        row={row}
                        field="hortonwood"
                        scanned={row.hortonwoodScan}
                        timestamp={row.hortonwoodScanTime}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <ScanStatus
                        row={row}
                        field="despatch"
                        scanned={row.despatchScan}
                        timestamp={row.despatchScanTime}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <OverallStatus status={row.overallStatus} />
                    </td>
                  </tr>
                ))}
                {!loading && !filteredRows.length ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-10 text-center text-gray-500">
                      No loaded barcode instances match the current filters.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <div className="flex items-center justify-center gap-3">
          {hasMore ? (
            <button
              type="button"
              onClick={loadMore}
              disabled={loading}
              className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-hover disabled:opacity-50"
            >
              {loading ? "Loading…" : "Load more"}
            </button>
          ) : null}
          {loading ? (
            <span className="text-sm text-gray-600">Loading barcode records…</span>
          ) : null}
        </div>
      </div>

      <DetailDrawer row={selectedRow} onClose={() => setSelectedRow(null)} />
    </main>
  );
}
